import { defineComponent as _defineComponent } from 'vue'
import {
    ref,
    PropType,
    toRefs,
    computed,
    onMounted,
    nextTick,
  } from "vue";
  import Modal from "@/components/ui/Modal.vue";
  import DropDown from "@/components/ui/dropdown/DropDown.vue";

  interface Column {
    value: string;
    isSelected: boolean;
    required: boolean;
  }

  interface CustomColumn {
    name: string;
    type: string;
  }

  
export default /*@__PURE__*/_defineComponent({
  __name: 'CustomizeColumnsModal',
  props: {
    isOpen: Boolean,
    showClose: { type: Boolean, default: true },
    columns: { type: Array as PropType<Column[]>, required: true },
    customColumns: { type: Array as PropType<CustomColumn[]>, required: true },
    isUpdating: { type: Boolean, default: false },
  },
  emits: ["close", "update", "addCustom", "updateColumns"],
  setup(__props, { expose: __expose, emit: __emit }) {
  __expose();

  const props = __props;

  const { columns } = toRefs(props);

  const emits = __emit;

  const customColumnName = ref("");
  const customColumnType = ref("Text");
  const scrollContainer = ref<HTMLElement | null>(null);
  const canScrollDown = ref(false);
  const draggedColumn = ref<string | null>(null);
  const dragOverColumn = ref<string | null>(null);

  const columnTypes = ["Text", "Number", "Date", "Dollar"];

  const visibleColumns = computed(() => {
    return columns.value
      .filter((col) => col.isSelected)
      .sort(
        (a: Column & { order?: number }, b: Column & { order?: number }) =>
          (a.order || 999) - (b.order || 999)
      );
  });

  const hiddenColumns = computed(() =>
    columns.value.filter((col) => !col.isSelected && !col.required)
  );

  function toggleColumn(name: string) {
    const column = columns.value.find((col) => col.value === name);

    if (column) {
      emits("updateColumns", {
        type: column.isSelected ? "remove" : "add",
        name,
      });
    }
  }

  function removeColumn(name: string) {
    emits("updateColumns", { type: "remove", name });
  }

  function handleRemoveColumn(column: Column & { isCustom?: boolean }) {
    if (column.isCustom) {
      const customIndex = props.customColumns.findIndex(
        (c) => c.name === column.value
      );

      if (customIndex !== -1) {
        removeCustomColumn(customIndex);
      }
    } else {
      removeColumn(column.value);
    }
  }

  function restoreColumn(name: string) {
    emits("updateColumns", { type: "add", name });
  }

  function addColumn(col: { value: string; isSelected: boolean }) {
    emits("updateColumns", { type: "add", col });
  }

  function addCustomColumn() {
    if (!customColumnName.value.trim()) return;

    const customColumn = {
      type: customColumnType.value as "Text" | "Number" | "Date" | "Dollar",
      name: customColumnName.value.trim(),
      value: undefined,
    };

    emits("updateColumns", {
      type: "addCustom",
      customColumn: customColumn,
    });

    customColumnName.value = "";
    customColumnType.value = "Text";
  }

  function selectColumnType(type: string) {
    customColumnType.value = type;
  }

  function removeCustomColumn(idx: number) {
    emits("updateColumns", { type: "removeCustom", idx });
  }

  function resetColumns() {
    emits("updateColumns", { type: "reset" });
  }

  function onClose() {
    emits("close");
  }

  function onDone() {
    emits("close");
  }

  function checkScrollability() {
    if (scrollContainer.value) {
      const { scrollHeight, clientHeight, scrollTop } = scrollContainer.value;

      canScrollDown.value =
        scrollHeight > clientHeight &&
        scrollTop < scrollHeight - clientHeight - 5;
    }
  }

  function handleScroll() {
    checkScrollability();
  }

  function handleDragStart(event: DragEvent, column: Column) {
    draggedColumn.value = column.value;

    if (event.dataTransfer) {
      event.dataTransfer.effectAllowed = "move";
      event.dataTransfer.setData("text/plain", column.value);
    }
  }

  function handleDragOver(event: DragEvent, column: Column) {
    event.preventDefault();

    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = "move";
    }
  }

  function handleDragEnter(event: DragEvent, column: Column) {
    event.preventDefault();

    if (draggedColumn.value && draggedColumn.value !== column.value) {
      dragOverColumn.value = column.value;
    }
  }

  function handleDragLeave(event: DragEvent) {
    const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
    const x = event.clientX;
    const y = event.clientY;

    if (x < rect.left || x > rect.right || y < rect.top || y > rect.bottom) {
      dragOverColumn.value = null;
    }
  }

  function handleDrop(event: DragEvent, targetColumn: Column) {
    event.preventDefault();

    if (!draggedColumn.value || draggedColumn.value === targetColumn.value) {
      draggedColumn.value = null;
      dragOverColumn.value = null;
      return;
    }

    emits("updateColumns", {
      type: "reorder",
      draggedColumn: draggedColumn.value,
      targetColumn: targetColumn.value,
    });

    draggedColumn.value = null;
    dragOverColumn.value = null;
  }

  function handleDragEnd() {
    draggedColumn.value = null;
    dragOverColumn.value = null;
  }

  onMounted(() => {
    nextTick(() => {
      checkScrollability();
    });
  });

const __returned__ = { props, columns, emits, customColumnName, customColumnType, scrollContainer, canScrollDown, draggedColumn, dragOverColumn, columnTypes, visibleColumns, hiddenColumns, toggleColumn, removeColumn, handleRemoveColumn, restoreColumn, addColumn, addCustomColumn, selectColumnType, removeCustomColumn, resetColumns, onClose, onDone, checkScrollability, handleScroll, handleDragStart, handleDragOver, handleDragEnter, handleDragLeave, handleDrop, handleDragEnd, ref, PropType, toRefs, computed, onMounted, nextTick, Modal, DropDown }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})